import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { name: "首页", path: "/" },
  { name: "电视剧", path: "/category/tv" },
  { name: "电影", path: "/category/movie" },
  { name: "资讯", path: "/news" },
  { name: "关于我们", path: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setKeyword("");
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200 shadow-sm"
          : "bg-primary-950"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 rounded-md bg-accent-500 text-primary-950 flex items-center justify-center text-lg">
              <i className="ri-vip-crown-line" aria-hidden="true"></i>
            </span>
            <span
              className={`font-heading text-xl font-bold tracking-wide ${
                scrolled ? "text-foreground-950" : "text-background-50"
              }`}
            >
              赏金女王
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? scrolled
                        ? "text-accent-600 bg-accent-50"
                        : "text-accent-400 bg-primary-900"
                      : scrolled
                        ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                        : "text-background-100 hover:text-accent-300 hover:bg-primary-900"
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={submitSearch} className="relative">
              <i
                className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"
                aria-hidden="true"
              ></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视 / 资讯"
                aria-label="搜索影视或资讯"
                className="w-44 lg:w-56 pl-9 pr-3 py-2 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </form>
            <Link
              to="/search"
              aria-label="进入搜索页"
              className="w-9 h-9 rounded-full flex items-center justify-center bg-accent-500 text-primary-950 hover:bg-accent-600 transition-colors"
            >
              <i className="ri-search-2-line" aria-hidden="true"></i>
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开导航菜单"
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md text-2xl ${
              scrolled ? "text-foreground-900" : "text-background-50"
            }`}
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div ref={menuRef} className="md:hidden bg-background-50 border-t border-background-200 animate-fade-in">
          <div className="px-4 py-4 space-y-3">
            <form onSubmit={submitSearch} className="relative">
              <i
                className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"
                aria-hidden="true"
              ></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视 / 资讯"
                aria-label="搜索影视或资讯"
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-md text-sm font-medium border-b border-background-100 last:border-0 ${
                      isActive ? "text-accent-600" : "text-foreground-700"
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}